import { useApolloClient, useMutation } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { BoundObjectType } from '@generated/graphql';
import { HouseholdPortfolioChangeReasons } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioChange';
import { useHouseholdPortfolioCoordination } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioCoordinationContext';
import { buildDetachClientGroupPortfolioInput } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutationInput';
import { UPDATE_CLIENT_GROUP_PORTFOLIO } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutations';
import { loadHouseholdOrganizationId } from '@ovComponents/4-module/workflowCompletion/subSteps/householdModelPortfolioDraft/householdDraftStaging';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useSnackbar } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isModelChangeProposalInFlight } from '../modelChangeProposal/modelChangeProposalErrors';
import {
  type DetachModelPortfolioOptions,
  DetachModelPortfolioVisual,
} from './detachModelPortfolio.visual';
import {
  ARCHIVE_ENTITY_BOUND_MODEL,
  type ArchiveEntityBoundModelInput,
} from './queries';

export type DetachModelPortfolioProps = {
  options: DetachModelPortfolioOptions;
  /**
   * Household ClientGroup id (the workflow completion `objectId`). Falls back
   * to `workflowData.currentHouseholdClientGroupId`, matching initialize.
   */
  clientGroupId?: string;
  onNext: () => void;
  stepLoading?: boolean;
};

/**
 * Clears the household's model portfolio by attaching a null financial product,
 * retires the copy it was holding, then advances. Nothing is written to the
 * workflow context: detaching is terminal for this branch, and re-running the
 * step on a household that is already detached is harmless.
 */
export const DetachModelPortfolio = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: DetachModelPortfolioProps) => {
  const { workflowData } = useContext(WorkflowContext);
  const graphqlClient = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { notifyChange } = useHouseholdPortfolioCoordination();
  const { t } = useTranslation('workflowCompletions');

  const [updateClientGroupPortfolio, { loading: detachLoading }] = useMutation(
    UPDATE_CLIENT_GROUP_PORTFOLIO,
  );
  const [archiveEntityBoundModel, { loading: archiveLoading }] = useMutation(
    ARCHIVE_ENTITY_BOUND_MODEL,
  );

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  /**
   * Retires the copy of the model the household was holding, now that nothing
   * points at it.
   *
   * Best effort by design: the detach it follows has already landed, and every
   * attach now sends `replaceExistingBase`, so a copy left behind is retired the
   * next time the household is put on a model. A failure here is therefore said
   * out loud and left at that, rather than held against a step whose own work is
   * done.
   */
  const archiveHouseholdModel = async (householdId: string) => {
    let organizationId = workflowData.entityOrganizationId;

    if (!organizationId) {
      // The SELECT_MODEL_PORTFOLIO_EDIT_TYPE → DETACH branch never stages a
      // draft, so nothing on it has written an entity organization. The
      // household's own record still knows it, so ask there rather than
      // reaching for the active organization, which can differ from the entity
      // that owns the copy.
      try {
        organizationId =
          (await loadHouseholdOrganizationId(graphqlClient, householdId)) ??
          undefined;
      } catch (caught) {
        datadogLogs.logger.error(
          'Household model portfolio archive organization lookup failed',
          { clientGroupId: householdId },
          caught instanceof Error ? caught : undefined,
        );
      }
    }

    if (!organizationId) {
      // No organization to scope the archive to, and nothing the advisor can do
      // about it — the detach itself is done.
      datadogLogs.logger.warn(
        'Household model portfolio archive skipped: no entity organization',
        { clientGroupId: householdId },
      );
      return;
    }

    const input: ArchiveEntityBoundModelInput = {
      boundObjectType: BoundObjectType.Household,
      boundObjectId: householdId,
      organizationId,
    };

    try {
      // Nothing is read back: `archived: false` says the household had no copy
      // of its own, which is as good an outcome here as having archived one.
      await archiveEntityBoundModel({
        // The refusal this can come back with is worded for the advisor below;
        // the global handler would toast the raw message first.
        variables: { input, skipErrorHandler: true },
      });
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio archive failed',
        { clientGroupId: householdId },
        error instanceof Error ? error : undefined,
      );
      enqueueSnackbar(
        isModelChangeProposalInFlight(error)
          ? t('householdDetach.archiveProposalPending')
          : t('householdDetach.archiveWarning'),
        { variant: 'warning' },
      );
    }
  };

  const handleDetach = async () => {
    if (!householdClientGroupId) {
      // The workflow is not scoped to a household, so there is nothing to
      // detach from. Distinct copy from a failed detach: retrying won't help.
      enqueueSnackbar(t('householdDetach.missingHousehold'), {
        variant: 'error',
      });
      return;
    }

    try {
      await updateClientGroupPortfolio({
        variables: {
          input: buildDetachClientGroupPortfolioInput(householdClientGroupId),
        },
      });
      // The mutation only returns the client group id, so nothing tells the
      // cache the portfolio is gone. The household widgets watch for this the
      // same way they do when the portfolio page detaches.
      notifyChange(HouseholdPortfolioChangeReasons.detach);
      // Pointer first, archive second, and not the other way around: archiving
      // first would leave the household pointing at an archived model portfolio
      // if the detach then failed, where this order leaves at worst a copy no
      // one holds — which is what a detach leaves behind today, and recoverable.
      await archiveHouseholdModel(householdClientGroupId);
      sendAnalytic(ovAnalyticsEvents.householdDetachCommit);
      onNext();
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio detach failed',
        { clientGroupId: householdClientGroupId },
        error instanceof Error ? error : undefined,
      );
      enqueueSnackbar(t('householdDetach.detachError'), { variant: 'error' });
    }
  };

  return (
    <DetachModelPortfolioVisual
      options={options}
      loading={stepLoading || detachLoading || archiveLoading}
      onNext={handleDetach}
    />
  );
};

export default DetachModelPortfolio;
